import { createSlice } from '@reduxjs/toolkit';
import {
  getROIFirstLevelOptions,
  getROIReturnOptions,
  getBenchmarkingFirstLevelOptions,
  getTimeLineFirstLevelOptions,
} from '../../pages/licenseAnalytics/licenseAnalyticsSupport/filtersOptions';
import { ACTUALBUDGET, GlobalStoreType, PAGE } from '../../pages/licenseAnalytics/licenseAnalyticsSupport/typeInterfaces';
import { OPTIONSENUM, headingsValue } from '../../pages/licenseAnalytics/licenseAnalyticsSupport/filtersOptions';
import { sortAllClubs } from '../../pages/licenseAnalytics/licenseAnalyticsSupport/calculationsUtils';
import {
  getYearValue,
  getFinanceReponse,
  getActualBudgetValue,
  getClubOptions,
  getSelectedPage,
  getFilteredLeaguesHistory,
} from '../../pages/licenseAnalytics/licenseAnalyticsSupport/getLicenseAnalyticsActions';

// this is initial state
const initialState: GlobalStoreType = {
  selectedPage: PAGE.FINANCE,
  teamsResponse: [],
  facilityResponse: { data: [] },
  staffResponse: [],
  playersResponse: [],
  playersCountResponse: [],
  financeResponse: [],
  colorsList: {
    avg: '#ED8A19', //yellow
    club: '#2E93fA', //blue
    '⭐': '#ed262c', //red
    '⭐⭐': '#00864e', //green
    '⭐⭐⭐': '#2b2829', //back
    '⭐⭐⭐⭐': '#800080', //purple
    'J1 League': '#ed262c', //red
    'J2 League': '#00864e', //green
    'J3 League': '#2b2829', //black
    Training: '#800080',
  },
  leaguesHistory: [],
  globalFilterOptions: {
    league: {
      options: [],
      value: [],
    },
    rating: {
      options: [
        { id: 1, name: '⭐', value: 1 },
        { id: 2, name: '⭐⭐', value: 2 },
        { id: 3, name: '⭐⭐⭐', value: 3 },
        { id: 4, name: '⭐⭐⭐⭐', value: 4 },
      ],
      value: [],
    },
    year: {
      options: [],
      value: { id: new Date().getFullYear(), name: new Date().getFullYear().toString() },
    },
    club: {
      options: [],
      value: undefined,
    },
    actualOrBudget: ACTUALBUDGET.ACTUAL,
    search: '',
  },
  timeLineData: {
    timeLine: {
      value: headingsValue[OPTIONSENUM.ACADEMYPROFITABILITY],
      secondValue: undefined,
    },
  },
  roiData: {
    roiInvestment: {
      value: headingsValue[OPTIONSENUM.ACADEMYPROFITABILITY],
      secondValue: undefined,
    },
    roiReturn: {
      value: headingsValue[OPTIONSENUM.PLAYERSPRODUCED],
    },
  },
  ranking: {
    rankingLables: [
      OPTIONSENUM.CLUBSPEND,
      OPTIONSENUM.ACADEMYSPEND,
      OPTIONSENUM.CLUBSPENDONACADEMY,
      OPTIONSENUM.LABOURCOST,
      OPTIONSENUM.ACADEMYINCOME,
    ],
    rank: {
      [OPTIONSENUM.CLUBSPEND]: [],
      [OPTIONSENUM.ACADEMYSPEND]: [],
      [OPTIONSENUM.CLUBSPENDONACADEMY]: [],
      [OPTIONSENUM.LABOURCOST]: [],
      [OPTIONSENUM.ACADEMYINCOME]: [],
    },
  },
  staffRanking: {
    rankingLabels: [
      OPTIONSENUM.TOTALSTAFF,
      OPTIONSENUM.STAFFCOSTS,
      OPTIONSENUM.QUALIFIEDCOACHES,
      OPTIONSENUM.COACHESSAQUALIFIED,
      OPTIONSENUM.AVERAGESALARY,
    ],
    rank: {
      [OPTIONSENUM.TOTALSTAFF]: [],
      [OPTIONSENUM.STAFFCOSTS]: [],
      [OPTIONSENUM.QUALIFIEDCOACHES]: [],
      [OPTIONSENUM.AVERAGESALARY]: [],
    },
  },
  facilityRanking: {
    rankingLabels: [
      OPTIONSENUM.FACILITESCOSTS,
      OPTIONSENUM.WITHMEDICALROOMS,
      OPTIONSENUM.CHANGINGROOMS,
      OPTIONSENUM.NATURALPITCHES,
      OPTIONSENUM.ARTIFICIALPITCHES,
    ],
    rank: {
      [OPTIONSENUM.FACILITESCOSTS]: [],
      [OPTIONSENUM.WITHMEDICALROOMS]: [],
      [OPTIONSENUM.CHANGINGROOMS]: [],
      [OPTIONSENUM.NATURALPITCHES]: [],
      [OPTIONSENUM.ARTIFICIALPITCHES]: [],
    },
  },
  teamsRanking: {
    rankingLabels: [
      OPTIONSENUM.TOTALTEAMS,
      OPTIONSENUM.TOTALEXPENSES,
      OPTIONSENUM.ADMISSIONFEES,
      OPTIONSENUM.TOTALPLAYERS,
      OPTIONSENUM.TRAININGHOURS,
    ],
    rank: {
      [OPTIONSENUM.TOTALTEAMS]: [],
      [OPTIONSENUM.TOTALEXPENSES]: [],
      [OPTIONSENUM.ADMISSIONFEES]: [],
      [OPTIONSENUM.TOTALPLAYERS]: [],
      [OPTIONSENUM.TRAININGHOURS]: [],
    },
  },
  playersRanking: {
    rankingLabels: [
      OPTIONSENUM.INLAST2YEARS,
      OPTIONSENUM.INLAST5YEARS,
      OPTIONSENUM.AVERAGETIMEATACADEMY,
      OPTIONSENUM.INTERNATIONALPLAYERS,
      OPTIONSENUM.YOUTHINTERNATIONALLAST5YEARS,
    ],
    rank: {
      [OPTIONSENUM.INLAST2YEARS]: [],
      [OPTIONSENUM.INLAST5YEARS]: [],
      [OPTIONSENUM.AVERAGETIMEATACADEMY]: [],
      [OPTIONSENUM.INTERNATIONALPLAYERS]: [],
      [OPTIONSENUM.YOUTHINTERNATIONALLAST5YEARS]: [],
    },
  },
  benchmarkingData: {
    benchmarking: {
      value: headingsValue[OPTIONSENUM.ACADEMYPROFITABILITY],
      secondOptions: [],
      secondValue: undefined,
    },
  },
  explorePage: {
    academyExpenseIncome: undefined,
  },
  departments: [],
};

// create slice for license analytics
// it is similar to reducer and actions together
const licenceAnalytics = createSlice({
  name: 'license analytics',
  initialState: initialState,
  reducers: {
    setPage(state:any, action): void {
      state.selectedPage = action.payload.page;
      state.timeLineData.timeLine.value = getTimeLineFirstLevelOptions(state.selectedPage)?.[0];

      state.roiData.roiInvestment.value = getROIFirstLevelOptions(state.selectedPage)?.[0];

      state.roiData.roiReturn.value = getROIReturnOptions(state.selectedPage)?.[0];
      state.benchmarkingData.benchmarking.value = getBenchmarkingFirstLevelOptions(state.selectedPage)?.[0];
    },
    setLeaguesHistory(state, action): void {
      state.leaguesHistory = action.payload.newValue;
    },
    setLeagueOptions(state, action): void {
      state.globalFilterOptions.league.options = action.payload.newValue;
    },
    setLeagueValue(state, action): void {
      state.globalFilterOptions.rating.value = [];
      state.globalFilterOptions.league.value = action.payload.newValue;
    },
    setRatingValue(state, action): void {
      state.globalFilterOptions.league.value = [];
      state.globalFilterOptions.rating.value = action.payload.newValue;
    },
    setYearOptions(state, action): void {
      state.globalFilterOptions.year.options = action.payload.options;
      if (action.payload.options?.length > 0) {
        state.globalFilterOptions.year.value = action.payload.options[0];
      }
    },
    setYearValue(state, action): void {
      state.globalFilterOptions.year.value = action.payload.newValue;
    },
    setSearchValue(state, action): void {
      state.globalFilterOptions.search = action.payload.newValue;
    },
    setClubOptions(state: GlobalStoreType, action): void {
      state.globalFilterOptions.club.options = action.payload.newValue;
      state.ranking.rank = sortAllClubs(
        getSelectedPage(state),
        getYearValue(state),
        getFinanceReponse(state),
        getActualBudgetValue(state),
        getClubOptions(state),
        getFilteredLeaguesHistory(state),
      );
    },
    setClubValue(state, action): void {
      state.globalFilterOptions.club.value = action.payload.newValue;
    },
    setActualOrBudgetValue(state, action): void {
      state.globalFilterOptions.actualOrBudget = action.payload.newValue;
    },
    setTimeLineValue(state, action): void {
      state.timeLineData.timeLine.value = action.payload.newValue;
      state.timeLineData.timeLine.secondValue = undefined;
    },
    setTimeLineSecondValue(state, action): void {
      state.timeLineData.timeLine.secondValue = action.payload.newValue;
    },
    setRoiValue(state, action): void {
      state.roiData.roiInvestment.value = action.payload.newValue;
      state.roiData.roiInvestment.secondValue = undefined;
    },
    setRoiSecondValue(state, action): void {
      state.roiData.roiInvestment.secondValue = action.payload.newValue;
    },
    setRoiReturnValue(state, action): void {
      state.roiData.roiReturn.value = action.payload.newValue;
    },
    setBenchmarkingValue(state, action): void {
      state.benchmarkingData.benchmarking.value = action.payload.newValue;
    },
    setBenchmarkingSecondValue(state, action): void {
      state.benchmarkingData.benchmarking.secondValue = action.payload.newValue;
    },
    setFinanceResponse(state, action): void {
      state.financeResponse = action.payload.response;
      state.ranking.rank = sortAllClubs(
        getSelectedPage(state),
        getYearValue(state),
        getFinanceReponse(state),
        getActualBudgetValue(state),
        getClubOptions(state),
        getFilteredLeaguesHistory(state),
      );
    },
    setExploreAcademyExpenseIncome(state, action): void {
      state.explorePage.academyExpenseIncome = action.payload.clubData;
    },
    setFacilitiesResponse(state, action): void {
      state.facilityResponse = action.payload.response;
    },
    setStaffResponse(state, action): void {
      state.staffResponse = action.payload.response;
    },
    setTeamsResponse(state, action): void {
      state.teamsResponse = action.payload.response;
    },
    setSupportFundResponse(state, action): void {
      state.supportFundResponse = action.payload.response;
    },
    setPlayersResponse(state, action): void {
      state.playersResponse = action.payload.response;
    },
    setPlayersCountResponse(state, action): void {
      state.playersCountResponse = action.payload.response;
    },
    setDepartmentsResponse(state, action): void {
      state.departments = action.payload.response;
    },
  },
});

export const { 
    setPage,
    setLeaguesHistory,
    setLeagueOptions,
    setLeagueValue,
    setRatingValue,
    setYearOptions,
    setYearValue,
    setSearchValue,
    setClubOptions,
    setClubValue,
    setActualOrBudgetValue,
    setTimeLineValue,
    setTimeLineSecondValue,
    setRoiValue,
    setRoiSecondValue,
    setRoiReturnValue,
    setBenchmarkingValue,
    setBenchmarkingSecondValue,
    setFinanceResponse,
    setExploreAcademyExpenseIncome,
    setFacilitiesResponse,
    setStaffResponse,
    setTeamsResponse,
    setSupportFundResponse,
    setPlayersResponse,
    setPlayersCountResponse,
    setDepartmentsResponse
 } = licenceAnalytics.actions;
 
export default licenceAnalytics.reducer;
